// gaWidget.ts
const BRIDGE_ORIGIN = 'https://ga-bridge.theassetmanager.co.uk';
const BRIDGE_URL = `${BRIDGE_ORIGIN}/bridge.html`;

let iframePromise: Promise<Window> | null = null;

// const log = console.log.bind(console, '[GA Widget]');
const log = (...args: any[]) => {}; // silence GA Widget logs

function loadIframe(): Promise<Window> {
  if (iframePromise) return iframePromise;
  iframePromise = new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.src = BRIDGE_URL;
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
    log('Iframe appended, waiting for load:', BRIDGE_URL);
    iframe.onload = () => {
      log('Iframe loaded');
      resolve(iframe.contentWindow!);
    };
  });
  return iframePromise;
}

async function postToIframe<T = any>(message: any): Promise<T> {
  if (typeof window === 'undefined') return Promise.reject('No window');
  log('Posting message to iframe:', message);
  const iframeWin = await loadIframe();
  return new Promise<T>((resolve) => {
    function handler(event: MessageEvent) {
      if (event.origin !== BRIDGE_ORIGIN) return;
      if (event.data?.type === 'gaStatus') {
        window.removeEventListener('message', handler);
        log('Received gaStatus from iframe:', event.data);
        resolve(event.data as T);
      }
    }
    window.addEventListener('message', handler);
    iframeWin.postMessage(message, BRIDGE_ORIGIN);
  });
}

function loadGoogleAnalytics(propertyId: string) {
  if (typeof window === 'undefined') return;
  log('Loading Google Analytics for property:', propertyId);
  const s = document.createElement('script');
  s.src = `https://www.googletagmanager.com/gtag/js?id=${propertyId}`;
  s.async = true;
  document.head.appendChild(s);

  (window as any).dataLayer = (window as any).dataLayer || [];
  function gtag(...args: any[]) { (window as any).dataLayer.push(args); }
  (window as any).gtag = gtag;

  gtag('js', new Date());
  gtag('config', propertyId);
  log('gtag initialised');
}

export const gaWidget = {
  /** Fetch flag, then conditionally load GA */
  async init(propertyId: string) {
    if (typeof window === 'undefined') return;
    log('gaWidget.init called with propertyId:', propertyId);
    const { ga_enabled } = await postToIframe<{ ga_enabled: boolean }>({ type: 'getGaStatus' });
    log('ga_enabled status:', ga_enabled);
    if (ga_enabled) loadGoogleAnalytics(propertyId);
    else log('GA not enabled, skipping GA load');
  },
  /** Turn GA *on* for this user */
  async enable() {
    if (typeof window === 'undefined') return;
    log('Enabling GA for user');
    await postToIframe({ type: 'setGaStatus', ga_enabled: true });
  },
  /** Turn GA *off* for this user */
  async disable() {
    if (typeof window === 'undefined') return;
    log('Disabling GA for user');
    await postToIframe({ type: 'setGaStatus', ga_enabled: false });
  },
  async getGaStatus(): Promise<{ ga_enabled: boolean }> {
    if (typeof window === 'undefined') return { ga_enabled: false };
    log('Getting GA status');
    return await postToIframe<{ ga_enabled: boolean }>({ type: 'getGaStatus' });
  }
};

window.$gaWidget = gaWidget;

// Assume $gaWidget is available on window
// Types for $gaWidget (you can refine as needed)
declare global {
  interface Window {
    $gaWidget: {
      getGaStatus: () => Promise<{ ga_enabled: boolean }>;
      enable  (): Promise<void>
      disable (): Promise<void>
    };
  }
}